import type { ReactElement } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Collapse } from 'reactstrap';
import { useAccordion } from 'atom-components/accordion/accordion';
import type { BaseNotification } from 'services/assist-api/entities/notification/check';
import { useDismissNotification } from 'services/assist-api/entities/notification/dismissed';
import { useAuth } from 'services/providers/auth.provider';
import './notification.scss';

type Props = { notification: BaseNotification };

export function BannerNotification({ notification }: Props): ReactElement | null {
  const { me } = useAuth();
  const isDismissable = notification.dismissable;
  const dismiss = useDismissNotification();

  if (isDismissable && dismiss.isSuccess) {
    return null;
  }

  return (
    <div className={clsx('card my-2 notification notification-banner', notification.severity)}>
      <div className="card-header">
        <span className="notification-title">{notification.name}</span>
        {isDismissable && !dismiss.isLoading && (
          <button
            type="button"
            onClick={(): void => dismiss.mutate({ notification: notification.id, user: me?.id })}
            className="btn btn-notification-close"
            title="Dismiss notification"
          >
            <FontAwesomeIcon icon="circle-xmark" size="lg" />
          </button>
        )}
        {isDismissable && dismiss.isLoading && <FontAwesomeIcon icon="spinner" spin />}
      </div>
      {notification.description && (
        <div className="card-body" dangerouslySetInnerHTML={{ __html: notification.description }} />
      )}
    </div>
  );
}

function severityToLoginClass(severity: string): string {
  switch (severity) {
    case 'error':
      return 'alert-danger';
    default:
      return `alert-${severity}`;
  }
}

export function LoginNotification({ notification }: Props): ReactElement {
  return (
    <div className={clsx('alert my-2', severityToLoginClass(notification.severity))}>
      <div className="alert-heading fw-bold p-2">{notification.name}</div> <hr />
      <div
        className="card-body p-2"
        dangerouslySetInnerHTML={{ __html: notification.description }}
      />
    </div>
  );
}

export function Notification({ notification }: Props): ReactElement | null {
  const { me } = useAuth();
  const isDismissable = notification.dismissable;
  const dismiss = useDismissNotification();
  const [buttonProps, contentProps] = useAccordion({
    initialOpen: notification.severity === 'error'
  });
  if (isDismissable && dismiss.isSuccess) {
    return null;
  }

  return (
    <div className={clsx('card notification notification-expandable', notification.severity)}>
      <div className="card-header">
        <button
          type="button"
          id={buttonProps.id}
          onClick={buttonProps.onClick}
          aria-expanded={buttonProps['aria-expanded']}
          aria-controls={buttonProps['aria-controls']}
          className="btn btn-accordion-control"
        >
          {notification.name}
          <FontAwesomeIcon icon={buttonProps['aria-expanded'] ? 'compress-alt' : 'expand-alt'} />
        </button>

        {isDismissable && !dismiss.isLoading && (
          <button
            type="button"
            onClick={(): void => dismiss.mutate({ notification: notification.id, user: me?.id })}
            className="btn btn-notification-close"
            title="Dismiss notification"
          >
            <FontAwesomeIcon icon="circle-xmark" />
          </button>
        )}
        {isDismissable && dismiss.isLoading && <FontAwesomeIcon icon="spinner" spin />}
      </div>
      <Collapse {...contentProps} isOpen={!contentProps['aria-hidden']} className="card-body">
        <div dangerouslySetInnerHTML={{ __html: notification.description }} />
      </Collapse>
    </div>
  );
}

export function NotificationList({
  notifications,
  NotificationComponent = Notification
}: {
  notifications?: BaseNotification[];
  NotificationComponent?: ({
    notification
  }: {
    notification: BaseNotification;
  }) => JSX.Element | null;
}): ReactElement {
  return (
    <div className="notification-list">
      {notifications?.map((notification) => (
        <NotificationComponent key={notification.id} notification={notification} />
      ))}
    </div>
  );
}
